// src/components/ui/QuoteModal.jsx  <-- NOTE THE .jsx EXTENSION

import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const equipmentOptions = [
  { value: 'high-peak-tent-10x10', label: 'High-Peak Tent (10x10m)' },
  { value: 'a-frame-marquee-15x30', label: 'A-Frame Marquee (15x30m)' },
  { value: 'lounge-sofa', label: 'Luxury Lounge Sofa' },
  { value: 'cocktail-table', label: 'Highboy Cocktail Table' },
  { value: 'chiavari-chair', label: 'Chiavari Chair (Gold)' },
  { value: 'dining-table-8-seater', label: '8-Seater Dining Table' },
  { value: 'led-par-can', label: 'LED Par Can Light' },
  { value: 'pa-system', label: 'PA System with 2 Speakers' },
  { value: 'led-screen-4x3', label: 'LED Screen (4x3m)' },
  { value: 'dance-floor-panel', label: 'Dance Floor Panel (1x1m)' },
  { value: 'stage-deck-2x1', label: 'Stage Deck (2x1m)' },
];

const QuoteModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    eventDate: '',
    venue: '',
    message: ''
  });
  const [rentedItems, setRentedItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({ equipment: '', quantity: 1 });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleCurrentItemChange = (field, value) => {
    setCurrentItem(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    if (!currentItem.equipment || currentItem.quantity < 1) {
      setErrors(prev => ({ ...prev, items: 'Please select a valid item and quantity.' }));
      return;
    }
    const selectedOption = equipmentOptions.find(opt => opt.value === currentItem.equipment);
    setRentedItems(prev => [...prev, { ...selectedOption, quantity: currentItem.quantity }]);
    setCurrentItem({ equipment: '', quantity: 1 });
    setErrors(prev => ({ ...prev, items: null }));
  };

  const handleRemoveItem = (value) => {
    setRentedItems(prev => prev.filter(item => item.value !== value));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.eventDate) newErrors.eventDate = 'Event date is required';
    if (rentedItems.length === 0) newErrors.items = 'Please add at least one item to the list.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    const whatsappNumber = "+254727937010";

    let message = `*📋 Equipment Rental Quote Request 📋*\n\n`;
    message += `*Client Name:* ${formData.name}\n`;
    message += `*Contact Phone:* ${formData.phone}\n`;
    message += `*Event Date:* ${formData.eventDate}\n`;
    if (formData.venue) message += `*Venue/Location:* ${formData.venue}\n`;
    
    message += `\n*Requested Items:*\n`;
    rentedItems.forEach(item => {
      message += `  - *${item.label}* (Quantity: ${item.quantity})\n`;
    });

    if (formData.message) {
      message += `\n*Additional Message:*\n${formData.message}`;
    }

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-serif font-bold text-2xl text-gray-900">Request a Quote</h2>
              <p className="font-sans text-gray-500 mt-1">Build your rental list and get a quote from our team.</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className=" -mt-2 -mr-2">
              <Icon name="X" size={24} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Full Name" type="text" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={errors.name} placeholder="Your Name" />
            <Input label="Phone Number" type="tel" required value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={errors.phone} placeholder="0712 345 678" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Event Date" type="date" required value={formData.eventDate} onChange={(e) => handleInputChange('eventDate', e.target.value)} error={errors.eventDate} min={new Date().toISOString().split('T')[0]} />
            <Input label="Venue / Location (Optional)" type="text" value={formData.venue} onChange={(e) => handleInputChange('venue', e.target.value)} placeholder="e.g., Nairobi, Kenya" />
          </div>

          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-bold text-gray-800">Build Your Rental List</h3>
              <div className="flex items-end gap-3">
                  <div className="flex-grow">
                      <Select label="Select Equipment" options={equipmentOptions} value={currentItem.equipment} onChange={(value) => handleCurrentItemChange('equipment', value)} placeholder="Choose an item..." />
                  </div>
                  <div className="w-24">
                      <Input label="Qty" type="number" min="1" value={currentItem.quantity} onChange={(e) => handleCurrentItemChange('quantity', parseInt(e.target.value, 10))} />
                  </div>
                  <Button type="button" onClick={handleAddItem} iconName="Plus" iconPosition="left">Add</Button>
              </div>
              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}
          </div>

          {rentedItems.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700">Your List:</h4>
              <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                {rentedItems.map((item) => (
                  <li key={item.value} className="p-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-800">{item.label}</p>
                      <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                    </div>
                    <Button variant="ghost" size="icon" type="button" onClick={() => handleRemoveItem(item.value)}>
                      <Icon name="Trash2" className="h-4 w-4 text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div>
            <label className="font-sans font-medium text-gray-800 mb-2 block">Additional Details (Optional)</label>
            <textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} placeholder="Any special requirements or questions?" rows={3} className="w-full p-3 border border-gray-300 rounded-lg font-sans text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none" />
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <Button type="submit" variant="primary" loading={isSubmitting} fullWidth size="lg">
              {isSubmitting ? 'Sending...' : 'Send Quote Request via WhatsApp'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;